/**
 * ProviderDashboardPage — shell for the provider CRM.
 * Phase 1 shows: registration prompt, leads inbox. Deeper CRM (quotes,
 * messages, calendar, analytics) tabs to be added in Phase 2.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { trackIntel } from "@/lib/analytics";
import useTrackPageView from "@/hooks/useTrackPageView";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, MapPin, Calendar, Clock, IndianRupee } from "lucide-react";
import ShareMenu from "@/components/ShareMenu";

export default function ProviderDashboardPage() {
  const [provider, setProvider] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([
        http.get("/sp-services/providers/me").catch(() => null),
        http.get("/sp-services/categories"),
      ]);
      setCategories(c.data.categories || []);
      if (p) {
        setProvider(p.data.provider);
        const l = await http.get("/sp-services/providers/me/leads");
        setLeads(l.data.leads || []);
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  if (!provider) return <ProviderRegisterForm categories={categories} onRegistered={load} />;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8" data-testid="provider-dashboard">
      <div className="mb-6 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{provider.business_name}</h1>
          <div className="text-sm text-slate-600 mt-1">
            {provider.category_slugs?.length || 0} service(s) · {provider.city || "location not set"} · Rating {provider.rating_avg > 0 ? provider.rating_avg.toFixed(1) : "—"}
          </div>
        </div>
        <ShareMenu
          url={`/p/${provider.id}`}
          title={provider.business_name}
          text={`${provider.business_name} — book via SocialPulse Services`}
          entityType="provider"
          entityId={provider.id}
          testid="provider-share"
          buttonLabel="Share profile"
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-3 mb-8">
        <StatCard label="New leads" value={leads.filter(l => l.provider_status === "pending").length} />
        <StatCard label="Quoted" value={leads.filter(l => l.provider_status === "quoted").length} />
        <StatCard label="Total jobs" value={provider.jobs_completed || 0} />
      </div>

      <Card className="p-4 sm:p-5">
        <h2 className="font-semibold mb-3">Leads</h2>
        {leads.length === 0 && (
          <div className="text-sm text-slate-500 py-6 text-center">
            No leads yet. When a customer submits a matching request, it will appear here.
          </div>
        )}
        <div className="space-y-3">
          {leads.map(l => (
            <div
              key={l.id}
              className="border rounded-md p-3 hover:bg-slate-50 cursor-pointer transition"
              onClick={() => nav(`/hire/rfs/${l.rfs.id}`)}
              data-testid={`lead-${l.rfs.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-medium">{l.rfs.title}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{l.rfs.category_name} · from {l.rfs.source_site}</div>
                </div>
                <div className="text-xs text-slate-500">Match {Math.round(l.score)}</div>
              </div>
              <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-600">
                {l.rfs.city && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{l.rfs.city}</span>}
                {l.rfs.requested_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{l.rfs.requested_date}</span>}
                {l.rfs.urgency && l.rfs.urgency !== "flexible" && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{l.rfs.urgency}</span>}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <Card className="p-4">
      <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
      <div className="text-3xl font-semibold mt-1">{value}</div>
    </Card>
  );
}

function ProviderRegisterForm({ categories, onRegistered }) {
  const [form, setForm] = useState({
    business_name: "", provider_type: "individual", description: "",
    category_slugs: [], city: "", region: "", country: "India",
    service_radius_km: 25, phone: "", whatsapp: "", website: "",
    languages: [], remote_available: false,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const toggleCat = (slug) => {
    setForm(f => ({
      ...f,
      category_slugs: f.category_slugs.includes(slug) ? f.category_slugs.filter(s => s !== slug) : [...f.category_slugs, slug]
    }));
  };
  const toggleLang = (l) => {
    setForm(f => ({
      ...f,
      languages: f.languages.includes(l) ? f.languages.filter(x => x !== l) : [...f.languages, l]
    }));
  };

  const submit = async () => {
    setErr("");
    if (!form.business_name.trim()) { setErr("Enter your business or professional name."); return; }
    if (form.category_slugs.length === 0) { setErr("Pick at least one service category."); return; }
    setSaving(true);
    try {
      await http.post("/sp-services/providers", form);
      trackIntel("provider_registered", { categories: form.category_slugs, city: form.city });
      onRegistered();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't register. Please try again.");
      setSaving(false);
    }
  };

  const leaves = categories.filter(c => c.parent_slug);

  return (
    <div className="max-w-2xl mx-auto px-4 py-8" data-testid="provider-register-form">
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2">Become a SocialPulse Services provider</h1>
      <p className="text-sm text-slate-600 mb-6">List your services and start receiving qualified leads from across the SocialPulse network — free during Phase 1.</p>

      <Card className="p-6 space-y-5">
        <div className="space-y-1.5">
          <Label>Business / professional name<span className="text-red-500">*</span></Label>
          <Input value={form.business_name} onChange={e => setForm({ ...form, business_name: e.target.value })} data-testid="provider-name" />
        </div>

        <div className="space-y-1.5">
          <Label>Short description</Label>
          <Textarea rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="What do you do? Who do you serve?" />
        </div>

        <div className="space-y-1.5">
          <Label>Services offered<span className="text-red-500">*</span></Label>
          <div className="flex flex-wrap gap-1.5">
            {leaves.map(c => {
              const active = form.category_slugs.includes(c.slug);
              return (
                <button
                  key={c.slug}
                  type="button"
                  onClick={() => toggleCat(c.slug)}
                  data-testid={`provider-cat-${c.slug}`}
                  className={`px-3 py-1.5 rounded-full text-xs border transition ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"}`}
                >
                  {c.name}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>City</Label>
            <Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} data-testid="provider-city" />
          </div>
          <div className="space-y-1.5">
            <Label>State / region</Label>
            <Input value={form.region} onChange={e => setForm({ ...form, region: e.target.value })} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Phone</Label>
            <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label>WhatsApp</Label>
            <Input value={form.whatsapp} onChange={e => setForm({ ...form, whatsapp: e.target.value })} />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>Languages</Label>
          <div className="flex flex-wrap gap-1.5">
            {["English", "Malayalam", "Hindi", "Tamil", "Arabic", "French", "Spanish"].map(l => {
              const active = form.languages.includes(l);
              return (
                <button
                  key={l}
                  type="button"
                  onClick={() => toggleLang(l)}
                  className={`px-3 py-1.5 rounded-full text-xs border transition ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"}`}
                >
                  {l}
                </button>
              );
            })}
          </div>
        </div>

        {err && <div className="text-sm text-red-600">{err}</div>}

        <Button onClick={submit} disabled={saving} className="w-full" data-testid="provider-submit">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Register as a provider
        </Button>
      </Card>
    </div>
  );
}
